import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Button, Card, Chip, ErrorNotice, Input, Notice, PageHeader, Skeleton, Stat, Table } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';
import { dateOnly, money, parseSar, toSarInput } from '@/lib/format';
import type { MembershipsOverview, Tier } from '@/lib/types';
import { ValidationError, describeError, type Issue } from './common';

type Draft = Record<string, { nameAr: string; nameEn: string; price: string; slots: string; active: boolean }>;

export function AdminMemberships() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const overview = useQuery({ queryKey: ['admin', 'memberships'], queryFn: () => api.get<MembershipsOverview>('/api/admin/memberships') });
  const tiers = useQuery({ queryKey: ['admin', 'tiers'], queryFn: () => api.get<{ items: Tier[] }>('/api/admin/tiers') });
  const [draft, setDraft] = useState<Draft>({});
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (tiers.data) setDraft(Object.fromEntries(tiers.data.items.map((tier) => [tier.id, { nameAr: tier.nameAr, nameEn: tier.nameEn, price: toSarInput(tier.priceHalalas), slots: String(tier.slots), active: tier.active }])));
  }, [tiers.data]);
  const changed = (tier: Tier) => {
    const d = draft[tier.id];
    return !!d && (d.nameAr !== tier.nameAr || d.nameEn !== tier.nameEn || parseSar(d.price) !== tier.priceHalalas || Number(d.slots) !== tier.slots || d.active !== tier.active);
  };
  // What the API accepts (membershipTierInput): names, a price of zero or more, a whole number of slots from 1 to 500.
  const SLOTS = { min: 1, max: 500 };
  const priceError = (v: string) => (v.trim() === '' ? t('admin.validation.required') : !Number.isFinite(Number(v.replace(/[^\d.]/g, ''))) || !/^[\d,.\s]+$/.test(v.trim()) ? t('admin.validation.nonNegative') : null);
  const slotsError = (v: string) => (v.trim() === '' ? t('admin.validation.required') : !/^\d+$/.test(v.trim()) || Number(v) < SLOTS.min || Number(v) > SLOTS.max ? t('admin.validation.integerRange', SLOTS) : null);
  const nameError = (v: string) => (v.trim() === '' ? t('admin.validation.required') : null);
  const fieldLabels: Record<string, string> = { nameAr: t('admin.memberships.nameArEn'), nameEn: t('admin.memberships.nameArEn'), priceHalalas: t('admin.memberships.monthlyPrice'), slots: t('admin.memberships.slots') };
  const rowIssues = (tier: Tier, d: Draft[string]): Issue[] => {
    const name = locale === 'ar' ? tier.nameAr : tier.nameEn;
    return [
      { path: 'nameAr', message: nameError(d.nameAr) },
      { path: 'nameEn', message: nameError(d.nameEn) },
      { path: 'priceHalalas', message: priceError(d.price) },
      { path: 'slots', message: slotsError(d.slots) },
    ]
      .filter((i): i is Issue => !!i.message)
      .map((i) => ({ path: t('admin.validation.tierRow', { tier: name, field: fieldLabels[i.path] ?? i.path }), message: i.message }));
  };
  const save = useMutation({
    mutationFn: async () => {
      const pending = (tiers.data?.items ?? []).filter(changed);
      const issues = pending.flatMap((tier) => rowIssues(tier, draft[tier.id]!));
      if (issues.length) throw new ValidationError(issues);
      for (const tier of pending) {
        const d = draft[tier.id]!;
        await api.patch(`/api/admin/tiers/${tier.id}`, { nameAr: d.nameAr.trim(), nameEn: d.nameEn.trim(), priceHalalas: parseSar(d.price) ?? 0, slots: Number(d.slots), active: d.active });
      }
    },
    onSuccess: () => { setSaved(true); void qc.invalidateQueries({ queryKey: ['admin'] }); },
  });
  if (overview.isLoading || tiers.isLoading) return <Skeleton lines={8} />;
  if (overview.error || tiers.error) return <ErrorNotice error={overview.error ?? tiers.error} retry={() => void overview.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} />;
  const o = overview.data!;
  const dirty = (tiers.data?.items ?? []).some(changed);
  const tierTable = (role: 'broker' | 'evaluator', title: string) => (
    <Card>
      <h2 className="mb-3">{title}</h2>
      <Table>
        <thead><tr><th className="w-[36%]">{t('admin.memberships.nameArEn')}</th><th>{t('admin.memberships.monthlyPrice')}</th><th>{t('admin.memberships.slots')}</th><th>{t('admin.memberships.members')}</th><th>{t('admin.memberships.enabled')}</th></tr></thead>
        <tbody>
          {(tiers.data?.items ?? []).filter((x) => x.role === role).map((tier) => {
            const d = draft[tier.id];
            if (!d) return null;
            const set = (patch: Partial<Draft[string]>) => setDraft({ ...draft, [tier.id]: { ...d, ...patch } });
            return (
              <tr key={tier.id}>
                <td><div className="flex gap-2"><Input className="w-[130px]" value={d.nameAr} invalid={!!nameError(d.nameAr)} onChange={(e) => set({ nameAr: e.target.value })} aria-label="Arabic" /><Input className="w-[130px]" dir="ltr" value={d.nameEn} invalid={!!nameError(d.nameEn)} onChange={(e) => set({ nameEn: e.target.value })} aria-label="English" /></div></td>
                <td><div className="flex items-center gap-1.5"><Input className="w-[100px] num" dir="ltr" inputMode="numeric" value={d.price} invalid={!!priceError(d.price)} onChange={(e) => set({ price: e.target.value })} aria-label={t('admin.memberships.monthlyPrice')} /> {t('app.sar')}</div></td>
                <td><Input className="w-[80px] num" dir="ltr" inputMode="numeric" value={d.slots} invalid={!!slotsError(d.slots)} aria-invalid={!!slotsError(d.slots)} onChange={(e) => set({ slots: e.target.value })} aria-label={t('admin.memberships.slots')} /></td>
                <td className="num">{o.membersByTier[tier.id] ?? 0}</td>
                <td><label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={d.active} onChange={(e) => set({ active: e.target.checked })} /> {d.active ? t('app.yes') : t('app.no')}</label></td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/admin', label: t('nav.dashboard') }, { label: t('nav.memberships') }]} title={t('nav.memberships')} meta={t('admin.memberships.intro')} actions={<Button variant="primary" className="min-h-9 text-[14px]" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>{t('app.saveChanges')}</Button>} />
      {saved && !dirty ? <Notice tone="success">{t('admin.memberships.saved')}</Notice> : null}
      {save.error ? <Notice tone="error">{describeError(save.error, t, fieldLabels)}</Notice> : null}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Stat label={t('admin.memberships.activeMembers')} value={o.stats.activeBrokers + o.stats.activeEvaluators} hint={`${t('dashboard.brokersN', { n: o.stats.activeBrokers })} · ${t('dashboard.evaluatorsN', { n: o.stats.activeEvaluators })}`} />
        <Stat label={t('admin.memberships.revenueMonth')} value={money(o.stats.revenueMonthHalalas, locale)} hint={t('admin.memberships.renewalsN', { n: o.stats.paymentsMonth })} />
        <Stat label={t('admin.memberships.pastDue')} value={o.stats.pastDue} tone={o.stats.pastDue ? 'warning' : undefined} />
      </div>
      {tierTable('broker', t('admin.memberships.brokerTiers'))}
      {tierTable('evaluator', t('admin.memberships.evaluatorTiers'))}
      <Card>
        <h2 className="mb-3">{t('admin.memberships.members')}</h2>
        {o.members.length ? (
          <Table>
            <thead><tr><th>{t('admin.memberships.member')}</th><th>{t('app.kind')}</th><th>{t('admin.user.tier')}</th><th>{t('app.status')}</th><th>{t('admin.memberships.nextRenewal')}</th><th>{t('admin.memberships.lastPayment')}</th></tr></thead>
            <tbody>
              {o.members.map((m) => (
                <tr key={m.userId}>
                  <td><Link to={`/admin/users/${m.userId}`}>{m.name}</Link></td>
                  <td>{t(`roles.${m.role}`)}</td>
                  <td>{m.tier ? (locale === 'ar' ? m.tier.nameAr : m.tier.nameEn) : '—'}</td>
                  <td>{m.membershipStatus === 'active' ? <Chip form="filled">{t('status.activeMembership')}</Chip> : m.membershipStatus === 'past_due' ? <Chip form="warning">{t('status.pastDue')}</Chip> : <Chip form="muted">{t(`status.${m.membershipStatus === 'cancelled' ? 'cancelledMembership' : 'expiredMembership'}`)}</Chip>}</td>
                  <td className="whitespace-nowrap tabular-nums">{m.membershipStatus !== 'active' ? '—' : m.autoRenew === false ? t('broker.membership.endsOn', { date: dateOnly(m.periodEnd, locale) }) : dateOnly(m.periodEnd, locale)}</td>
                  <td className="num">{m.lastPayment ? `${m.lastPayment.number} · ${m.lastPayment.status === 'paid' ? money(m.lastPayment.amountHalalas, locale) : t(`status.${m.lastPayment.status}`)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : <p className="text-muted text-[14px]">{t('app.none')}</p>}
      </Card>
    </div>
  );
}
